/**
 * GSAP motion for the platform. Transform and opacity only; everything is
 * visible without script, and prefers-reduced-motion skips the travel.
 *
 *   const scope = useReveal([data]);   <section ref={scope}> … <div data-reveal> …
 *
 * Elements marked data-reveal rise into place in reading order, staggered.
 * data-reveal-group="x" children animate together as one step.
 */
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(useGSAP);

export const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function useReveal(deps = [], { y = 18, stagger = 0.07, delay = 0.05 } = {}) {
  const scope = useRef(null);
  useGSAP(() => {
    if (!scope.current || prefersReducedMotion()) return;
    const items = scope.current.querySelectorAll("[data-reveal]:not([data-revealed])");
    if (!items.length) return;
    items.forEach((el) => el.setAttribute("data-revealed", ""));
    gsap.fromTo(items, { autoAlpha: 0, y }, {
      autoAlpha: 1, y: 0, duration: 0.6, ease: "power3.out", stagger, delay, clearProps: "transform",
    });
  }, { scope, dependencies: deps });
  return scope;
}

/** Draw the path rail and light each star in turn, like walking the route. */
export function usePathMotion(deps = []) {
  const scope = useRef(null);
  useGSAP(() => {
    const root = scope.current;
    if (!root || prefersReducedMotion()) return;
    const bar = root.querySelector(".path-rail-line");
    const stars = root.querySelectorAll(".path-star svg");
    const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
    if (bar) tl.fromTo(bar, { scaleY: 0, transformOrigin: "top" }, { scaleY: 1, duration: 0.9 });
    if (stars.length) tl.fromTo(stars, { scale: 0.4, rotate: -45, autoAlpha: 0 },
      { scale: 1, rotate: 0, autoAlpha: 1, duration: 0.45, stagger: 0.12, ease: "back.out(2)" }, 0.1);
  }, { scope, dependencies: deps });
  return scope;
}

/** Count a number up once it is on screen. */
export function useCountUp(value, deps = []) {
  const ref = useRef(null);
  useGSAP(() => {
    const el = ref.current;
    if (!el) return;
    const to = Number(value) || 0;
    if (prefersReducedMotion()) { el.textContent = String(to); return; }
    const state = { n: 0 };
    gsap.to(state, { n: to, duration: 1.1, ease: "power2.out", onUpdate: () => { el.textContent = String(Math.round(state.n)); } });
  }, { dependencies: [value, ...deps] });
  return ref;
}

/**
 * Page entrance on every route change: the page's top-level blocks rise in
 * order. Blocks that manage their own reveal (data-reveal) are left alone.
 */
export function usePageEnter(key) {
  const scope = useRef(null);
  useGSAP(() => {
    const root = scope.current;
    if (!root || prefersReducedMotion()) return;
    const page = root.firstElementChild;
    if (!page) return;
    const blocks = [...page.children].filter((el) => !el.hasAttribute("data-reveal") && !el.querySelector(":scope > [data-reveal]"));
    gsap.fromTo(blocks.length ? blocks : [page], { autoAlpha: 0, y: 16 },
      { autoAlpha: 1, y: 0, duration: 0.55, ease: "power3.out", stagger: 0.06, clearProps: "transform,opacity,visibility" });
  }, { scope, dependencies: [key], revertOnUpdate: true });
  return scope;
}
